import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../../db';
import type {
  CachedOperatorCombo,
  ComboWeatherSnapshot as CachedComboWeatherSnapshot,
  OperatorComboCompletion,
  OperatorComboStart,
} from '../../db';
import {
  applyLocalStartedCombo,
  applyLocalTerminalCombo,
  getOperatorComboWithFallback,
} from '../../services/operatorSpraySessionCacheService';
import {
  operatorSpraySessionService,
  MixPreview,
} from '../../services/operatorSpraySessionService';
import { sprayService, Orchard, SprayMachine } from '../../services/sprayService';
import { syncPendingSprayCompletions } from '../../services/spraySyncService';
import { useAuth } from '../../contexts/AuthContext';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import PageHeader from '../shell/PageHeader';
import { Banner, DefinitionList, DataPair, Field, FormSection } from '../patterns';
import SessionStatusPill from './statusPills/SessionStatusPill';
import OpenSignOffWeatherPanel, { SprayWeatherSnapshotInput } from './OpenSignOffWeatherPanel';
import CloseSignOffWeatherPanel, { CloseSignOffWeatherSnapshot } from './CloseSignOffWeatherPanel';
import { MixAmountsModal } from './MixAmountsModal';
import { resolveComboIntensity } from '../../utils/resolveComboIntensity';

/**
 * OperatorComboWorkFlow — the per-combo Field Mode work-flow page,
 * mounted at `/protection/operator-sessions/:comboId`. Phase 6 / Task 6.14
 * rewrite (previous 614-line implementation discarded).
 *
 * Data model: reads from the Dexie `operatorCombos` cache via
 * `useLiveQuery`; writes ONLY to the local queue tables
 * (`operatorComboStarts`, `operatorComboCompletes`) via the optimistic
 * cache writers in `operatorSpraySessionCacheService`. The drainer in
 * `spraySyncService` picks up the queue entries and replays them to the
 * backend when connectivity is available — this surface never POSTs
 * directly to the lifecycle endpoints.
 *
 * State machine (derived):
 *   loading              — cache lookup pending on first paint
 *   not_started          — combo is `not_started` with no queued Start
 *   in_progress_menu     — combo is `in_progress` (live OR optimistic via
 *                          a queued Start) with no terminal-bound queue
 *                          entry and no inline form open
 *   in_progress_form_*   — operator opened the Complete / End-early /
 *                          Abandon inline form
 *   queued_readonly      — a Complete/EndEarly/Abandon entry is queued
 *                          but unsynced — page locks to a read-only
 *                          "What you submitted" summary until the drainer
 *                          succeeds
 *   terminal             — cached combo is in {completed, incomplete,
 *                          cancelled}
 *
 * Operator-self gate: actions are hidden unless the caller is the linked
 * SprayOperator OR a Farm Admin. The backend mirrors the rule on every
 * lifecycle endpoint — the UI hide is defence-in-depth + UX.
 *
 * Identity in chrome: PageHeader renders `SES-{seq} · {sessionName}` from
 * the cached combo's parent metadata.
 *
 * Mix amounts: a "View mix amounts" affordance lives in PageHeader
 * actions; the modal itself is Task 6.15. For Task 6.14 we expose the
 * button + state shell; the modal is stubbed.
 *
 * Draft persistence: every form-field change writes to
 * `db.operatorComboDrafts` so leaving the page and coming back restores
 * the operator's typed input. Read once on mount as a synchronous seed.
 */

type RenderState =
  | 'loading'
  | 'not_started'
  | 'in_progress_menu'
  | 'in_progress_form_complete'
  | 'in_progress_form_end_early'
  | 'in_progress_form_abandon'
  | 'terminal'
  | 'queued_readonly';

// In-flight = the drainer hasn't given up yet. A quarantined queue
// entry means the server REJECTED the submission (4xx) and the drainer
// has reverted the optimistic state flip — so the page must fall
// through to the canonical (server-truth) renderState, not pin to the
// optimistic one. The quarantined entry survives in its own live-query
// so the rejection banner can render the server's error.
const IN_FLIGHT_STATUSES: Array<OperatorComboStart['syncStatus']> = [
  'pending',
  'syncing',
  'failed',
];

const COMPLETE_MIN_TANK_COUNT = 0.1;

const TERMINAL_STATUSES: Array<CachedOperatorCombo['status']> = [
  'completed',
  'incomplete',
  'cancelled',
];

const ABANDON_MIN_REASON_LENGTH = 3;

/**
 * Format an ISO timestamp as `HH:MM, YYYY-MM-DD` for the live "Now:" chip
 * and the terminal recorded-values summary. Mirrors
 * OperatorSessionList.formatStartedTime so the two surfaces present
 * timestamps identically.
 */
function formatNowChip(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const HH = String(d.getHours()).padStart(2, '0');
  const MM = String(d.getMinutes()).padStart(2, '0');
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${HH}:${MM}, ${yyyy}-${mm}-${dd}`;
}

/**
 * Convert an ISO timestamp into the value form expected by an
 * `<input type="datetime-local">` (yyyy-MM-ddTHH:mm in local time).
 */
function isoToDateTimeLocal(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function nowAsDateTimeLocal(): string {
  return isoToDateTimeLocal(new Date().toISOString());
}

// Backend's startedAt sanity bound is `now − 90 days` (see
// backend/src/services/operatorSpraySessionService.js SANITY_PAST_MS).
// Mirror it client-side so the operator can't even pick a guaranteed-
// reject value in the datetime picker.
const SANITY_PAST_MS = 90 * 24 * 60 * 60 * 1000;
function ninetyDaysAgoAsDateTimeLocal(): string {
  return isoToDateTimeLocal(new Date(Date.now() - SANITY_PAST_MS).toISOString());
}

/**
 * Translate a datetime-local string ("yyyy-MM-ddTHH:mm" in local time)
 * back into a full ISO-8601 string. Returns '' on parse failure.
 */
function dateTimeLocalToIso(value: string): string {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString();
}

/**
 * Build the `ComboWeatherSnapshot` payload (cached/queue shape) from the
 * close-weather panel's snapshot. The two shapes are intentionally
 * symmetric — the panel's `temperatureCategory` etc. become the queue
 * row's `temperatureCategory`, etc. — but we strip empty strings to
 * leave the discriminator fields well-formed if the operator hasn't yet
 * picked a value.
 */
function closeWeatherToCombo(
  snapshot: CloseSignOffWeatherSnapshot | null,
): CachedComboWeatherSnapshot | undefined {
  if (!snapshot) return undefined;
  if (!snapshot.temperatureCategory || !snapshot.windCategory || !snapshot.precipitationCategory) {
    return undefined;
  }
  const out: CachedComboWeatherSnapshot = {
    temperatureCategory: snapshot.temperatureCategory,
    windCategory: snapshot.windCategory,
    precipitationCategory: snapshot.precipitationCategory,
  };
  if (snapshot.systemReported) {
    const sr = snapshot.systemReported;
    if (
      typeof sr.temp === 'number' &&
      typeof sr.windSpeed === 'number' &&
      typeof sr.precipitation === 'number'
    ) {
      out.systemReported = {
        temp: sr.temp,
        windSpeed: sr.windSpeed,
        precipitation: sr.precipitation,
      };
    }
  }
  if (snapshot.overrideComment) out.overrideComment = snapshot.overrideComment;
  return out;
}

/**
 * Build the `ComboWeatherSnapshot` payload from the open-weather panel's
 * snapshot. The open panel uses `finalSelected.{temperature,wind,precipitation}`
 * — translate into the queue shape.
 */
function openWeatherToCombo(
  snapshot: SprayWeatherSnapshotInput | null,
): CachedComboWeatherSnapshot | undefined {
  if (!snapshot) return undefined;
  const { temperature, wind, precipitation } = snapshot.finalSelected;
  if (!temperature || !wind || !precipitation) return undefined;
  const out: CachedComboWeatherSnapshot = {
    temperatureCategory: temperature,
    windCategory: wind,
    precipitationCategory: precipitation,
  };
  if (snapshot.systemReported) {
    out.systemReported = {
      temp: snapshot.systemReported.temp,
      windSpeed: snapshot.systemReported.windSpeed,
      precipitation: snapshot.systemReported.precipitation,
    };
  }
  return out;
}

interface PpeChecks {
  [item: string]: boolean;
}

const round2 = (n: number): number => Number(n.toFixed(2));

const OperatorComboWorkFlow: React.FC = () => {
  const { t } = useTranslation();
  const { comboId } = useParams<{ comboId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  // ───────────────────────────────────────────────────────────────────
  // Cache reads (via useLiveQuery) — these drive renderState.
  // Explicit `T | undefined` annotations work around Dexie's
  // PromiseExtended return type leaking through the inferred result.
  // ───────────────────────────────────────────────────────────────────
  const cachedCombo: CachedOperatorCombo | undefined = useLiveQuery(
    async () => (comboId ? await db.operatorCombos.get(comboId) : undefined),
    [comboId],
  );

  const queuedStart: OperatorComboStart | undefined = useLiveQuery(
    async () => {
      if (!comboId) return undefined;
      const rows = await db.operatorComboStarts
        .where('comboId')
        .equals(comboId)
        .filter(r => (IN_FLIGHT_STATUSES as ReadonlyArray<string>).includes(r.syncStatus))
        .toArray();
      return rows[0];
    },
    [comboId],
  );

  const quarantinedStart: OperatorComboStart | undefined = useLiveQuery(
    async () => {
      if (!comboId) return undefined;
      const rows = await db.operatorComboStarts
        .where('comboId')
        .equals(comboId)
        .filter(r => r.syncStatus === 'quarantined')
        .toArray();
      return rows[0];
    },
    [comboId],
  );

  const queuedTerminal: OperatorComboCompletion | undefined = useLiveQuery(
    async () => {
      if (!comboId) return undefined;
      const rows = await db.operatorComboCompletes
        .where('comboId')
        .equals(comboId)
        .filter(r => (IN_FLIGHT_STATUSES as ReadonlyArray<string>).includes(r.syncStatus))
        .toArray();
      return rows[0];
    },
    [comboId],
  );

  const quarantinedTerminal: OperatorComboCompletion | undefined = useLiveQuery(
    async () => {
      if (!comboId) return undefined;
      const rows = await db.operatorComboCompletes
        .where('comboId')
        .equals(comboId)
        .filter(r => r.syncStatus === 'quarantined')
        .toArray();
      return rows[0];
    },
    [comboId],
  );

  // `undefined` and "no draft row" are DIFFERENT states and must not be
  // conflated: `useLiveQuery` yields `undefined` until the query first
  // resolves, so a querier that also returns `undefined` for a missing row
  // leaves callers unable to tell "not loaded yet" from "nothing to load".
  // Normalising the miss to `null` makes `undefined` mean exactly one
  // thing — still loading — which the two effects below depend on.
  const draft = useLiveQuery(
    async () => (comboId ? (await db.operatorComboDrafts.get(comboId)) ?? null : null),
    [comboId],
  );

  // ───────────────────────────────────────────────────────────────────
  // Background fetch of fresh combo on mount (silent on failure — the
  // cache IS the source of truth for this surface).
  // ───────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!comboId) return;
    getOperatorComboWithFallback(comboId).catch(() => {
      /* silent — useLiveQuery still serves the cached row */
    });
  }, [comboId]);

  // ───────────────────────────────────────────────────────────────────
  // Supporting context — orchards (for variety/area) + machines (for
  // tank volume) + operator-self gate. Best-effort: failures leave the
  // summary card with '—' placeholders.
  // ───────────────────────────────────────────────────────────────────
  const [orchards, setOrchards] = useState<Orchard[]>([]);
  const [machines, setMachines] = useState<SprayMachine[]>([]);
  const [callerOperatorId, setCallerOperatorId] = useState<string | null>(null);
  const [hasLinkedOperator, setHasLinkedOperator] = useState<boolean>(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [orchResp, machResp, myResp] = await Promise.all([
        sprayService.getOrchards().catch(() => [] as Orchard[]),
        sprayService.getMachines().catch(() => [] as SprayMachine[]),
        operatorSpraySessionService
          .getMyOperatorSessions()
          .catch(() => ({ sessions: [] as any[], hasLinkedOperator: false })),
      ]);
      if (cancelled) return;
      setOrchards(orchResp || []);
      setMachines(machResp || []);
      setHasLinkedOperator(myResp.hasLinkedOperator);
      setCallerOperatorId(myResp.sessions.length > 0 ? myResp.sessions[0].operatorId : null);
    })();
    return () => { cancelled = true; };
  }, [comboId]);

  // ───────────────────────────────────────────────────────────────────
  // Mix preview (drives the live "derived products" display).
  // ───────────────────────────────────────────────────────────────────
  const [mixPreview, setMixPreview] = useState<MixPreview | null>(null);
  const [mixPreviewError, setMixPreviewError] = useState(false);

  useEffect(() => {
    if (!comboId) return;
    let cancelled = false;
    setMixPreviewError(false);
    operatorSpraySessionService
      .getMixPreview(comboId)
      .then(p => {
        if (!cancelled) setMixPreview(p);
      })
      .catch(() => {
        if (!cancelled) setMixPreviewError(true);
      });
    return () => { cancelled = true; };
  }, [comboId]);

  // ───────────────────────────────────────────────────────────────────
  // Form-local state — see seeding from draft below.
  // ───────────────────────────────────────────────────────────────────
  const [ppeChecks, setPpeChecks] = useState<PpeChecks>({});
  const [startTime, setStartTime] = useState<string>('');
  const [endTime, setEndTime] = useState<string>('');
  const [tankCount, setTankCount] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [confirmedNoProductSprayed, setConfirmedNoProductSprayed] = useState<boolean>(false);
  const [activeAction, setActiveAction] = useState<'complete' | 'end-early' | 'abandon' | null>(null);
  const [openWeather, setOpenWeather] = useState<SprayWeatherSnapshotInput | null>(null);
  const [closeWeather, setCloseWeather] = useState<CloseSignOffWeatherSnapshot | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const [showMixModal, setShowMixModal] = useState(false);

  // ── Live "Now:" chip — refreshes every minute so the helper chip
  //    next to the datetime input doesn't drift while the form sits
  //    open.
  const [nowChip, setNowChip] = useState<string>(() => formatNowChip(new Date().toISOString()));
  useEffect(() => {
    const handle = setInterval(() => {
      setNowChip(formatNowChip(new Date().toISOString()));
    }, 60_000);
    return () => clearInterval(handle);
  }, []);

  // ── Default start/end time to "now" until the operator types
  //    something. We don't write the default to the draft — the draft
  //    only gets populated once the operator interacts.
  useEffect(() => {
    if (!startTime && cachedCombo?.status === 'not_started') {
      setStartTime(nowAsDateTimeLocal());
    }
  }, [startTime, cachedCombo?.status]);

  useEffect(() => {
    if (!endTime && activeAction && activeAction !== 'abandon') {
      setEndTime(nowAsDateTimeLocal());
    }
  }, [endTime, activeAction]);

  // ── Seed form state from the draft on first read. We do this
  //    exactly once when the live-query first resolves — subsequent
  //    draft writes are our own, so we don't re-seed.
  const [draftSeeded, setDraftSeeded] = useState(false);
  useEffect(() => {
    if (draftSeeded) return;
    if (draft === undefined) return; // live query hasn't resolved yet
    if (draft === null) { setDraftSeeded(true); return; } // resolved: no draft to restore
    if (draft.state.ppeChecks) setPpeChecks(draft.state.ppeChecks);
    if (draft.state.startTime) setStartTime(draft.state.startTime);
    if (draft.state.endTime) setEndTime(draft.state.endTime);
    if (draft.state.tankCount) setTankCount(draft.state.tankCount);
    if (draft.state.reason) setReason(draft.state.reason);
    if (draft.state.confirmedNoProductSprayed) setConfirmedNoProductSprayed(true);
    if (draft.state.activeAction) setActiveAction(draft.state.activeAction);
    setDraftSeeded(true);
  }, [draft, draftSeeded]);

  // ── Persist the form draft on every state change, but never before the
  //    seed above has run. Writing earlier is what made this component
  //    clobber its own state: the first render put an EMPTY draft, the
  //    live query then delivered that empty row back, and the seed effect
  //    restored `{}` over whatever the operator had entered in between —
  //    silently un-ticking PPE and leaving Start disabled with no way back.
  //
  //    `draft` is deliberately NOT a dependency. It was one, and since
  //    every write bumps `updatedAt`, each put produced a fresh emission
  //    that re-ran this effect and wrote again — an unbounded write loop
  //    (measured at ~49 puts/1.5s on an idle page) that starved the event
  //    loop and widened the race window above under CI contention.
  useEffect(() => {
    if (!comboId) return;
    if (!draftSeeded) return; // hold off until we've seeded
    const now = new Date().toISOString();
    db.operatorComboDrafts
      .put({
        comboId,
        state: {
          ppeChecks,
          startTime,
          endTime,
          tankCount,
          reason,
          confirmedNoProductSprayed,
          activeAction,
        },
        updatedAt: now,
      })
      .catch(() => {
        /* draft-write failures are non-fatal */
      });
  }, [comboId, draftSeeded, ppeChecks, startTime, endTime, tankCount, reason, confirmedNoProductSprayed, activeAction]);

  // ───────────────────────────────────────────────────────────────────
  // Operator-self gate
  // ───────────────────────────────────────────────────────────────────
  const isFarmAdmin = !!user?.role?.isFarmAdministrator;
  const canAct = useMemo(() => {
    if (!cachedCombo) return false;
    if (isFarmAdmin) return true;
    if (!hasLinkedOperator) return false;
    if (!callerOperatorId) return false;
    return callerOperatorId === cachedCombo.operatorId;
  }, [cachedCombo, isFarmAdmin, hasLinkedOperator, callerOperatorId]);

  // ───────────────────────────────────────────────────────────────────
  // RenderState derivation (§5.4 matrix)
  // ───────────────────────────────────────────────────────────────────
  const renderState: RenderState = useMemo(() => {
    if (!cachedCombo) return 'loading';
    if (queuedTerminal) return 'queued_readonly';
    if (TERMINAL_STATUSES.includes(cachedCombo.status)) return 'terminal';
    if (cachedCombo.status === 'not_started' && !queuedStart) return 'not_started';
    // in_progress (or optimistic in_progress from a queued Start)
    if (activeAction === 'complete') return 'in_progress_form_complete';
    if (activeAction === 'end-early') return 'in_progress_form_end_early';
    if (activeAction === 'abandon') return 'in_progress_form_abandon';
    return 'in_progress_menu';
  }, [cachedCombo, queuedStart, queuedTerminal, activeAction]);

  // ───────────────────────────────────────────────────────────────────
  // Breadcrumb (always — even while loading).
  // ───────────────────────────────────────────────────────────────────
  useBreadcrumb([
    { label: 'Citrus Synch', to: '/' },
    { label: t('spray.title'), to: '/protection' },
    { label: t('spray.operatorSessions.title'), to: '/protection/my-sessions' },
    { label: t('spray.operatorCombo.title') },
  ]);

  // ───────────────────────────────────────────────────────────────────
  // Resolved summary-card values (Program / Orchard / Machine / etc.)
  // ───────────────────────────────────────────────────────────────────
  const parent = cachedCombo?.sessionId;
  const programName = parent?.programId?.name || '—';
  // Phase 6b — intensity lives per-combo. Resolver returns combo's own
  // intensityLevel first, falling back to parent.intensityLevel for legacy
  // combos that pre-date Phase 6b. Returns null when neither is set;
  // render an em-dash for the operator-side display in that case.
  const intensityLevel = resolveComboIntensity(cachedCombo, parent) ?? '—';

  const orchardLabel = useMemo(() => {
    const firstOrchardId = parent?.orchardIds?.[0];
    if (!firstOrchardId) return '—';
    const o = orchards.find(o => o._id === firstOrchardId);
    if (!o) return '—';
    const parts: string[] = [o.orchardName ?? '—'];
    if (o.variety) parts.push(o.variety);
    if (typeof o.areaHectares === 'number') parts.push(`${o.areaHectares} ha`);
    return parts.join(' · ');
  }, [parent?.orchardIds, orchards]);

  const machineLabel = useMemo(() => {
    if (!cachedCombo?.machineId) return '—';
    const m = machines.find(x => x._id === cachedCombo.machineId);
    if (!m) return '—';
    const tank = typeof m.tankVolume === 'number' ? ` (${m.tankVolume} L tank)` : '';
    return `${m.name}${tank}`;
  }, [cachedCombo?.machineId, machines]);

  const identityLine = useMemo(() => {
    const seq = parent?.seq;
    const name = parent?.name;
    if (seq === undefined || seq === null) return t('spray.operatorCombo.title');
    return name ? `SES-${seq} · ${name}` : `SES-${seq}`;
  }, [parent?.name, parent?.seq, t]);

  // ───────────────────────────────────────────────────────────────────
  // Banners — queued / quarantined / late-signoff / not-self.
  // ───────────────────────────────────────────────────────────────────
  const showLateSignoff =
    cachedCombo?.status === 'in_progress' && parent?.status === 'cancelled';

  const dismissQuarantined = useCallback(async () => {
    if (quarantinedTerminal?.localId == null) return;
    await db.operatorComboCompletes.delete(quarantinedTerminal.localId);
  }, [quarantinedTerminal]);

  const dismissQuarantinedStart = useCallback(async () => {
    if (quarantinedStart?.localId == null) return;
    await db.operatorComboStarts.delete(quarantinedStart.localId);
  }, [quarantinedStart]);

  // ───────────────────────────────────────────────────────────────────
  // PPE-all-checked predicate
  // ───────────────────────────────────────────────────────────────────
  const ppeAllChecked = useMemo(() => {
    const checklist = cachedCombo?.ppeChecklist ?? [];
    if (checklist.length === 0) return true;
    return checklist.every(row => ppeChecks[row.item] === true);
  }, [cachedCombo, ppeChecks]);

  const startTimeTooOld = useMemo(() => {
    if (!startTime) return false;
    const iso = dateTimeLocalToIso(startTime);
    if (!iso) return false;
    return new Date(iso).getTime() < Date.now() - SANITY_PAST_MS;
  }, [startTime]);

  const tankCountNumeric = Number(tankCount) || 0;

  // ───────────────────────────────────────────────────────────────────
  // Submit handlers
  // ───────────────────────────────────────────────────────────────────
  const handleStart = useCallback(async () => {
    if (!comboId || !cachedCombo) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      const weatherStart = openWeatherToCombo(openWeather);
      const startedAt = startTime ? dateTimeLocalToIso(startTime) : undefined;
      const payload: OperatorComboStart['payload'] = {
        ppeChecks: (cachedCombo.ppeChecklist ?? []).map(row => ({
          item: row.item,
          checked: ppeChecks[row.item] === true,
        })),
        // The queue payload requires a non-optional weatherStart per the
        // table interface; an empty selection lands as undefined and is
        // a hard backend reject — but starting offline without weather
        // is a legitimate fallback the spec allows ("Reliability NFR").
        weatherStart: weatherStart ?? ({} as CachedComboWeatherSnapshot),
        ...(startedAt ? { startedAt } : {}),
      };
      await applyLocalStartedCombo(comboId, payload);
      // Best-effort: kick the drainer immediately if online so an
      // online operator sees their combo flip to in_progress on the
      // server within seconds.
      if (typeof navigator !== 'undefined' && navigator.onLine) {
        syncPendingSprayCompletions().catch(() => { /* drainer logs its own errors */ });
      }
      setActiveAction(null);
    } catch (err: any) {
      setSubmitError(err?.message ?? '');
    } finally {
      setSubmitting(false);
    }
  }, [comboId, cachedCombo, openWeather, startTime, ppeChecks]);

  const submitTerminal = useCallback(
    async (action: 'complete' | 'end-early' | 'abandon') => {
      if (!comboId) return;
      setSubmitting(true);
      setSubmitError('');
      try {
        const payload: OperatorComboCompletion['payload'] = {};
        if (action === 'abandon') {
          payload.reason = reason;
          payload.confirmedNoProductSprayed = true;
        } else {
          payload.tankCount = tankCountNumeric;
          payload.endTime = endTime ? dateTimeLocalToIso(endTime) : undefined;
          const weatherEnd = closeWeatherToCombo(closeWeather);
          if (weatherEnd) payload.weatherEnd = weatherEnd;
          if (action === 'end-early') payload.reason = reason;
        }
        await applyLocalTerminalCombo(comboId, action, payload);
        if (typeof navigator !== 'undefined' && navigator.onLine) {
          syncPendingSprayCompletions().catch(() => { /* drainer logs its own errors */ });
        }
        // Clear form-local state — page will re-render in
        // queued_readonly / terminal once the cache reflects the action.
        setActiveAction(null);
      } catch (err: any) {
        setSubmitError(err?.message ?? '');
      } finally {
        setSubmitting(false);
      }
    },
    [comboId, reason, tankCountNumeric, endTime, closeWeather],
  );

  // ───────────────────────────────────────────────────────────────────
  // Render
  // ───────────────────────────────────────────────────────────────────
  const header = (
    <PageHeader
      title={identityLine}
      meta={
        cachedCombo && (
          <span className="d-inline-flex flex-wrap gap-2 align-items-center">
            {parent?.status && <SessionStatusPill status={parent.status} />}
            <SessionStatusPill status={cachedCombo.status} />
          </span>
        )
      }
      actions={
        cachedCombo ? (
          <button
            type="button"
            className="pat-btn pat-btn--subtle"
            onClick={() => setShowMixModal(true)}
            data-testid="view-mix-amounts-button"
          >
            {t('spray.operatorCombo.actions.viewMixAmounts')}
          </button>
        ) : undefined
      }
    />
  );

  if (renderState === 'loading') {
    return (
      <div className="operator-combo-workflow">
        {header}
        <div className="text-center p-4" role="status">
          {t('common.loading')}
        </div>
      </div>
    );
  }

  const showNotSelfBanner = !canAct && cachedCombo && !TERMINAL_STATUSES.includes(cachedCombo.status);

  const summaryCard = (
    <FormSection id="combo-summary" title={t('spray.operatorCombo.summary.title')}>
      <DefinitionList columns={2}>
        <DataPair
          label={t('spray.operatorCombo.summary.program')}
          value={programName}
        />
        <DataPair
          label={t('spray.operatorCombo.summary.orchard')}
          value={orchardLabel}
        />
        <DataPair
          label={t('spray.operatorCombo.summary.machine')}
          value={machineLabel}
        />
        {cachedCombo?.startedAt && (
          <DataPair
            label={t('spray.operatorCombo.summary.started')}
            value={formatNowChip(cachedCombo.startedAt)}
          />
        )}
        <DataPair
          label={t('spray.operatorCombo.summary.intensity')}
          value={intensityLevel}
        />
      </DefinitionList>
    </FormSection>
  );

  // ── Conditional banners (top of page).
  const banners = (
    <>
      {showLateSignoff && (
        <Banner
          tone="danger"
          title={t('spray.operatorCombo.banners.lateSignoff.title')}
          detail={t('spray.operatorCombo.banners.lateSignoff.detail')}
          testId="banner-late-signoff"
        />
      )}
      {queuedTerminal && (
        <Banner
          tone="queued"
          title={t('spray.operatorCombo.banners.queued.title')}
          detail={t('spray.operatorCombo.banners.queued.detail')}
          testId="banner-queued"
        />
      )}
      {quarantinedTerminal && (
        <Banner
          tone="danger"
          title={t('spray.operatorCombo.banners.quarantined.title')}
          detail={t('spray.operatorCombo.banners.quarantined.detail', { error: quarantinedTerminal.lastError ?? '' })}
          actions={
            <button
              type="button"
              className="pat-btn pat-btn--subtle"
              onClick={dismissQuarantined}
              data-testid="banner-quarantined-dismiss"
            >
              {t('spray.operatorCombo.banners.quarantined.discard')}
            </button>
          }
          testId="banner-quarantined"
        />
      )}
      {quarantinedStart && (
        <Banner
          tone="danger"
          title={t('spray.operatorCombo.banners.quarantined.title')}
          detail={t('spray.operatorCombo.banners.quarantined.detail', { error: quarantinedStart.lastError ?? '' })}
          actions={
            <button
              type="button"
              className="pat-btn pat-btn--subtle"
              onClick={dismissQuarantinedStart}
              data-testid="banner-quarantined-start-dismiss"
            >
              {t('spray.operatorCombo.banners.quarantined.discard')}
            </button>
          }
          testId="banner-quarantined-start"
        />
      )}
      {showNotSelfBanner && (
        <Banner
          tone="offline"
          title={t('spray.operatorCombo.banners.notSelf.title')}
          detail={t('spray.operatorCombo.notYourComboBanner')}
          testId="banner-not-self"
        />
      )}
    </>
  );

  // ─────────────────────────────────────────────────────────────────
  //  Helpers for terminal + queued-readonly view rendering
  // ─────────────────────────────────────────────────────────────────
  const renderWeatherSummary = (snap: CachedComboWeatherSnapshot | undefined) => {
    if (!snap) return '—';
    return t('spray.operatorCombo.terminal.weatherUnit', {
      temperature: snap.temperatureCategory,
      wind: snap.windCategory,
      precipitation: snap.precipitationCategory,
    });
  };

  // ─────────────────────────────────────────────────────────────────
  //  Queued readonly view (terminal-bound queue entry present).
  // ─────────────────────────────────────────────────────────────────
  if (renderState === 'queued_readonly' && queuedTerminal) {
    const p = queuedTerminal.payload;
    return (
      <div className="operator-combo-workflow">
        {header}
        {banners}
        {summaryCard}
        <FormSection id="queued-readonly" title={t('spray.operatorCombo.queuedReadonly.title')}>
          <p className="text-muted small">{t('spray.operatorCombo.queuedReadonly.subtitle')}</p>
          <DefinitionList columns={2}>
            <DataPair
              label={t('spray.operatorCombo.queuedReadonly.action')}
              value={t(`spray.operatorCombo.queuedReadonly.actionLabel.${queuedTerminal.action}` as const)}
            />
            {p.endTime && (
              <DataPair
                label={t('spray.operatorCombo.terminal.endTime')}
                value={formatNowChip(p.endTime)}
              />
            )}
            {typeof p.tankCount === 'number' && (
              <DataPair
                label={t('spray.operatorCombo.terminal.tanksUsed')}
                value={String(p.tankCount)}
              />
            )}
            {p.weatherEnd && (
              <DataPair
                label={t('spray.operatorCombo.terminal.weatherEnd')}
                value={renderWeatherSummary(p.weatherEnd)}
              />
            )}
            {p.reason && (
              <DataPair
                label={t('spray.operatorCombo.terminal.reason')}
                value={p.reason}
              />
            )}
          </DefinitionList>
        </FormSection>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────
  //  Terminal view — read-only DefinitionList of recorded values.
  // ─────────────────────────────────────────────────────────────────
  if (renderState === 'terminal' && cachedCombo) {
    return (
      <div className="operator-combo-workflow">
        {header}
        {banners}
        {summaryCard}
        <FormSection id="terminal-summary" title={t('spray.operatorCombo.terminal.title')}>
          <DefinitionList columns={2}>
            {cachedCombo.endedAt && (
              <DataPair
                label={t('spray.operatorCombo.terminal.endTime')}
                value={formatNowChip(cachedCombo.endedAt)}
              />
            )}
            {cachedCombo.weatherSnapshots?.end && (
              <DataPair
                label={t('spray.operatorCombo.terminal.weatherEnd')}
                value={renderWeatherSummary(cachedCombo.weatherSnapshots.end)}
              />
            )}
          </DefinitionList>
        </FormSection>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────
  //  Editable states (not_started + in_progress menu/forms)
  // ─────────────────────────────────────────────────────────────────
  const handlePpeToggle = (item: string, checked: boolean) => {
    setPpeChecks(prev => ({ ...prev, [item]: checked }));
  };

  const startForm = (
    <>
      <FormSection id="combo-ppe" title={t('spray.operatorCombo.ppeTitle')} description={t('spray.operatorCombo.ppeIntro')}>
        <div className="d-flex flex-column gap-2">
          {(cachedCombo?.ppeChecklist ?? []).map(row => (
            <label
              key={row.item}
              htmlFor={`ppe-${row.item}`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                cursor: 'pointer',
                font: 'inherit',
                fontSize: 14,
                color: 'var(--shell-text-strong)',
              }}
            >
              <input
                id={`ppe-${row.item}`}
                type="checkbox"
                className="pat-checkbox"
                checked={ppeChecks[row.item] === true}
                onChange={e => handlePpeToggle(row.item, e.target.checked)}
              />
              <span>{row.item}</span>
            </label>
          ))}
        </div>
        {!ppeAllChecked && (
          <div className="text-warning small mt-2">
            {t('spray.operatorCombo.errors.ppeRequired')}
          </div>
        )}
      </FormSection>

      <FormSection
        id="combo-start-time"
        title={t('spray.operatorCombo.startTime.title')}
        description={t('spray.operatorCombo.startTime.now', { time: nowChip })}
      >
        <Field
          label={t('spray.operatorCombo.startTime.label')}
          controlId="combo-start-time-input"
        >
          <input
            id="combo-start-time-input"
            type="datetime-local"
            className="pat-input"
            value={startTime}
            min={ninetyDaysAgoAsDateTimeLocal()}
            max={nowAsDateTimeLocal()}
            onChange={e => setStartTime(e.target.value)}
            aria-label={t('spray.operatorCombo.startTime.label')}
          />
          {startTime && dateTimeLocalToIso(startTime) &&
            new Date(dateTimeLocalToIso(startTime)).getTime() < Date.now() - SANITY_PAST_MS && (
            <div
              className="text-danger small mt-1"
              data-testid="combo-start-time-too-old-error"
            >
              {t('spray.operatorCombo.startTime.tooOldError')}
            </div>
          )}
        </Field>
      </FormSection>

      <OpenSignOffWeatherPanel
        onChange={setOpenWeather}
        sessionId={comboId}
        startTime={startTime}
      />

      {submitError && (
        <Banner
          tone="danger"
          title={t('spray.operatorCombo.submitError', { message: submitError })}
          testId="banner-submit-error"
        />
      )}

      {canAct && (
        <div className="d-flex gap-2 mt-3">
          <button
            type="button"
            className="pat-btn pat-btn--subtle"
            onClick={() => navigate('/protection/my-sessions')}
            disabled={submitting}
          >
            {t('spray.operatorCombo.actions.cancel')}
          </button>
          <button
            type="button"
            className="pat-btn pat-btn--primary"
            disabled={!ppeAllChecked || submitting || startTimeTooOld}
            onClick={handleStart}
          >
            {t('spray.operatorCombo.actions.startSpraying')}
          </button>
        </div>
      )}
    </>
  );

  const actionMenu = (
    <div className="d-flex flex-wrap gap-2 mb-3" data-testid="combo-action-menu">
      <button
        type="button"
        className="pat-btn pat-btn--primary"
        onClick={() => setActiveAction('complete')}
      >
        {t('spray.operatorCombo.actions.complete')}
      </button>
      <button
        type="button"
        className="pat-btn pat-btn--subtle"
        onClick={() => setActiveAction('end-early')}
      >
        {t('spray.operatorCombo.actions.endEarly')}
      </button>
      <button
        type="button"
        className="pat-btn pat-btn--danger"
        onClick={() => setActiveAction('abandon')}
      >
        {t('spray.operatorCombo.actions.abandon')}
      </button>
    </div>
  );

  const derivedProductsBlock = (
    <FormSection id="combo-derived-products" title={t('spray.operatorCombo.derivedProducts.title')} description={t('spray.operatorCombo.derivedProducts.intro')}>
      {mixPreviewError ? (
        <p className="text-muted small mb-0">{t('spray.operatorCombo.derivedProducts.unavailable')}</p>
      ) : !mixPreview ? (
        <p className="text-muted small mb-0">{t('spray.operatorCombo.derivedProducts.loading')}</p>
      ) : (
        <ul className="ps-3 mb-0">
          {mixPreview.perTankMix.map(p => {
            const total = p.dosePerTank * tankCountNumeric;
            return (
              <li key={p.productId} data-testid={`derived-product-${p.productId}`}>
                {t('spray.operatorCombo.derivedProducts.formula', {
                  name: p.productName,
                  dosePerTank: round2(p.dosePerTank),
                  unit: p.doseUnit,
                  tanks: tankCountNumeric || 0,
                  total: round2(total),
                })}
              </li>
            );
          })}
        </ul>
      )}
    </FormSection>
  );

  const completeFormSubmitDisabled =
    submitting || tankCountNumeric < COMPLETE_MIN_TANK_COUNT;

  const completeForm = (action: 'complete' | 'end-early') => (
    <>
      {action === 'end-early' && (
        <FormSection
          id="combo-end-early-reason"
          title={t('spray.operatorCombo.endEarly.reasonLabel')}
        >
          <Field
            label={t('spray.operatorCombo.endEarly.reasonLabel')}
            controlId="combo-end-early-reason-input"
          >
            <input
              id="combo-end-early-reason-input"
              type="text"
              className="pat-input"
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder={t('spray.operatorCombo.endEarly.reasonPlaceholder')}
              aria-label={t('spray.operatorCombo.endEarly.reasonLabel')}
            />
          </Field>
        </FormSection>
      )}

      <FormSection
        id="combo-end-time"
        title={t('spray.operatorCombo.endTime.title')}
        description={t('spray.operatorCombo.endTime.now', { time: nowChip })}
      >
        <Field
          label={t('spray.operatorCombo.endTime.label')}
          controlId="combo-end-time-input"
        >
          <input
            id="combo-end-time-input"
            type="datetime-local"
            className="pat-input"
            value={endTime}
            max={nowAsDateTimeLocal()}
            onChange={e => setEndTime(e.target.value)}
            aria-label={t('spray.operatorCombo.endTime.label')}
          />
        </Field>
      </FormSection>

      <FormSection
        id="combo-tank-count"
        title={t('spray.operatorCombo.tankCount.title')}
        description={t('spray.operatorCombo.tankCount.helper')}
      >
        <Field
          label={t('spray.operatorCombo.tankCount.label')}
          controlId="combo-tank-count-input"
        >
          <input
            id="combo-tank-count-input"
            type="number"
            className="pat-input"
            min={0.1}
            step={0.1}
            value={tankCount}
            onChange={e => setTankCount(e.target.value)}
            placeholder={t('spray.operatorCombo.tankCount.placeholder')}
            aria-label={t('spray.operatorCombo.tankCount.label')}
          />
          {tankCountNumeric < COMPLETE_MIN_TANK_COUNT && (
            <div
              className="text-danger small mt-1"
              data-testid="combo-tank-count-error"
            >
              {t('spray.operatorCombo.tankCount.minError')}
            </div>
          )}
        </Field>
      </FormSection>

      {derivedProductsBlock}

      <CloseSignOffWeatherPanel
        scopeId={comboId!}
        endTime={endTime}
        onChange={setCloseWeather}
      />

      {submitError && (
        <Banner
          tone="danger"
          title={t('spray.operatorCombo.submitError', { message: submitError })}
          testId="banner-submit-error"
        />
      )}

      <div className="d-flex gap-2 mt-3">
        <button
          type="button"
          className="pat-btn pat-btn--subtle"
          onClick={() => { setActiveAction(null); setSubmitError(''); }}
          disabled={submitting}
        >
          {t('spray.operatorCombo.actions.cancel')}
        </button>
        <button
          type="button"
          className="pat-btn pat-btn--primary"
          onClick={() => submitTerminal(action)}
          disabled={completeFormSubmitDisabled}
        >
          {action === 'complete'
            ? t('spray.operatorCombo.actions.submitCompletion')
            : t('spray.operatorCombo.actions.submitEndEarly')}
        </button>
      </div>
    </>
  );

  const abandonForm = (
    <>
      <FormSection
        id="combo-abandon-reason"
        title={t('spray.operatorCombo.abandon.reasonLabel')}
      >
        <Field
          label={t('spray.operatorCombo.abandon.reasonLabel')}
          controlId="combo-abandon-reason-input"
        >
          <input
            id="combo-abandon-reason-input"
            type="text"
            className="pat-input"
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder={t('spray.operatorCombo.abandon.reasonPlaceholder')}
            aria-label={t('spray.operatorCombo.abandon.reasonLabel')}
          />
        </Field>
        <label
          htmlFor="combo-abandon-confirm"
          className="d-inline-flex align-items-center gap-2 mt-3"
          style={{ cursor: 'pointer' }}
        >
          <input
            id="combo-abandon-confirm"
            type="checkbox"
            className="pat-checkbox"
            checked={confirmedNoProductSprayed}
            onChange={e => setConfirmedNoProductSprayed(e.target.checked)}
          />
          <span>{t('spray.operatorCombo.abandon.noProductCheckbox')}</span>
        </label>
      </FormSection>

      {submitError && (
        <Banner
          tone="danger"
          title={t('spray.operatorCombo.submitError', { message: submitError })}
          testId="banner-submit-error"
        />
      )}

      <div className="d-flex gap-2 mt-3">
        <button
          type="button"
          className="pat-btn pat-btn--subtle"
          onClick={() => { setActiveAction(null); setSubmitError(''); }}
          disabled={submitting}
        >
          {t('spray.operatorCombo.actions.cancel')}
        </button>
        <button
          type="button"
          className="pat-btn pat-btn--danger"
          onClick={() => submitTerminal('abandon')}
          disabled={
            submitting ||
            reason.trim().length < ABANDON_MIN_REASON_LENGTH ||
            !confirmedNoProductSprayed
          }
        >
          {t('spray.operatorCombo.actions.submitAbandon')}
        </button>
      </div>
    </>
  );

  return (
    <div className="operator-combo-workflow">
      {header}
      {banners}
      {summaryCard}

      {/* not_started: PPE + Start time + Start weather + Start CTA */}
      {renderState === 'not_started' && canAct && startForm}
      {renderState === 'not_started' && !canAct && (
        <Banner
          tone="offline"
          title={t('spray.operatorCombo.readOnlyStatus')}
          testId="banner-read-only-status"
        />
      )}

      {/* in_progress menu + forms — only when caller can act. */}
      {renderState === 'in_progress_menu' && canAct && actionMenu}
      {renderState === 'in_progress_menu' && !canAct && (
        <Banner
          tone="offline"
          title={t('spray.operatorCombo.readOnlyStatus')}
          testId="banner-read-only-status"
        />
      )}
      {renderState === 'in_progress_form_complete' && canAct && completeForm('complete')}
      {renderState === 'in_progress_form_end_early' && canAct && completeForm('end-early')}
      {renderState === 'in_progress_form_abandon' && canAct && abandonForm}

      {/* Mix-amounts modal — Task 6.15. Mounted only when comboId is
          present so the modal can fetch /mix-preview on first open. */}
      {comboId && (
        <MixAmountsModal
          comboId={comboId}
          show={showMixModal}
          onHide={() => setShowMixModal(false)}
        />
      )}
    </div>
  );
};

export default OperatorComboWorkFlow;
